// Environment dressing: cartoon ocean water + distant islets + procedural low-poly vegetation.
// Everything is procedural (no texture/model downloads) and baked into a handful of merged
// meshes with vertex-attribute wind sway — cheap to render, cartoonish to look at.
import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { makeNoise } from './noise.js';

const mergeAll = (gs) => {
  const r = BufferGeometryUtils.mergeGeometries(gs.map((g) => (g.index ? g.toNonIndexed() : g)));
  if (!r) console.error('[env] merge failed', gs);
  return r;
};

function paintGeo(g, hex) {
  const c = new THREE.Color(hex);
  const n = g.attributes.position.count;
  const a = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { a[i * 3] = c.r; a[i * 3 + 1] = c.g; a[i * 3 + 2] = c.b; }
  g.setAttribute('color', new THREE.BufferAttribute(a, 3));
  return g;
}

/* ============ cartoon ocean ============ */
// Stylized water: animated waves, depth tint from the terrain height texture,
// noisy shore-foam bands, sun sparkle + fresnel. One draw call, no reflections render.
export function addWater(scene, terrain) {
  const span = terrain.worldSize * 3.2;
  const geo = new THREE.PlaneGeometry(span, span, 96, 96);
  geo.rotateX(-Math.PI / 2);
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    uniforms: {
      time: { value: 0 },
      heightMap: { value: terrain.heightTex },
      uHalfSpan: { value: terrain.heightTexSpan },
      cShallow: { value: new THREE.Color(0x9fdde7) },
      cDeep: { value: new THREE.Color(0x8ad2de) },
      cFoam: { value: new THREE.Color(0xffffff) },
    },
    vertexShader: `
      uniform float time;
      varying vec3 vW;
      varying vec3 vN;
      void main(){
        vec3 p = position;
        float w1 = sin(p.x*0.11 + time*1.1)*0.14;
        float w2 = sin(p.z*0.09 - time*0.8)*0.12;
        float w3 = sin((p.x+p.z)*0.2 + time*1.7)*0.07;
        p.y += w1+w2+w3;
        float dx = 0.11*cos(p.x*0.11+time*1.1)*0.14 + 0.2*cos((p.x+p.z)*0.2+time*1.7)*0.07;
        float dz = 0.09*cos(p.z*0.09-time*0.8)*0.12 + 0.2*cos((p.x+p.z)*0.2+time*1.7)*0.07;
        vN = normalize(vec3(-dx, 1.0, -dz));
        vec4 wp = modelMatrix*vec4(p,1.0);
        vW = wp.xyz;
        gl_Position = projectionMatrix*viewMatrix*wp;
      }`,
    fragmentShader: `
      uniform float time; uniform sampler2D heightMap; uniform float uHalfSpan;
      uniform vec3 cShallow,cDeep,cFoam;
      varying vec3 vW;
      varying vec3 vN;
      float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
      float vnoise(vec2 p){
        vec2 i=floor(p),f=fract(p);f=f*f*(3.0-2.0*f);
        return mix(mix(hash(i),hash(i+vec2(1,0)),f.x),mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),f.x),f.y);
      }
      void main(){
        vec2 uv = vW.xz/(2.0*uHalfSpan) + 0.5;
        float th = -6.5;
        if(uv.x>0.0&&uv.x<1.0&&uv.y>0.0&&uv.y<1.0) th = texture2D(heightMap,uv).r;
        vec2 b = min(uv, 1.0-uv);
        float edge = smoothstep(0.0, 0.05, min(b.x, b.y));
        th = mix(-6.5, th, clamp(edge,0.0,1.0));
        float depth = clamp((0.0-th)/5.0, 0.0, 1.0) * 0.75;
        vec3 col = mix(cShallow, cDeep, depth);
        float sp = vnoise(vW.xz*0.45 + vec2(time*0.5, -time*0.35));
        col += vec3(0.09)*smoothstep(0.78,0.95,sp);
        float shore = 1.0 - smoothstep(-1.4, 0.35, -th);
        float band = smoothstep(0.55,0.95, vnoise(vW.xz*0.7 + vec2(time*0.7,time*0.5)) * (0.6+0.4*sin(time*1.4+vW.x*0.2+vW.z*0.17)));
        float foam = clamp(shore*(0.5 + 0.7*band), 0.0, 1.0);
        col = mix(col, cFoam, foam*0.85);
        vec3 n = normalize(vN + 0.16*vec3(vnoise(vW.xz*0.6+time*0.4)-0.5, 0.0, vnoise(vW.xz*0.6-time*0.3)-0.5));
        vec3 viewDir = normalize(cameraPosition - vW);
        vec3 sunDir = normalize(vec3(24.0,34.0,16.0));
        float spec = pow(max(dot(reflect(-sunDir, n), viewDir), 0.0), 90.0);
        col += vec3(1.0,0.95,0.82) * spec * 0.7 * (1.0-foam);
        float fres = pow(1.0 - max(dot(n, viewDir), 0.0), 3.0);
        col = mix(col, vec3(0.78,0.92,0.94), fres*0.5);
        float alpha = mix(0.94, 0.8, 1.0-depth);
        gl_FragColor = vec4(col, alpha);
      }`,
  });
  const water = new THREE.Mesh(geo, mat);
  water.position.y = terrain.waterLevel;
  water.renderOrder = 1;
  scene.add(water);
  return water; // main loop advances water.material.uniforms.time.value
}

/* ============ distant islets (backdrop) ============ */
// A few tiny palm islands on the horizon instead of an endless land skirt — it's an island zoo.
export function addBackdrop(scene, terrain, seed = 5) {
  const rnd = (() => { let s = seed ^ 0x9e3779b9; return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296; })();
  const group = new THREE.Group();
  group.name = 'backdrop';
  const half = terrain.worldSize / 2;
  const parts = [];

  const islet = (cx, cz, r, h) => {
    const segs = 24;
    const g = new THREE.CircleGeometry(r * 2.2, segs);
    g.rotateX(-Math.PI / 2);
    const pos = g.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const dx = pos.getX(i), dz = pos.getZ(i);
      const d2 = (dx * dx + dz * dz) / (r * r);
      pos.setY(i, h * Math.exp(-d2 * 1.5) - 1.6);
    }
    g.computeVertexNormals();
    // sand -> grass tint by height
    const col = new THREE.Color();
    const a = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i);
      if (y < 0.5) col.setHex(0xf4e4b8); else col.setHex(0xaede90);
      a[i * 3] = col.r; a[i * 3 + 1] = col.g; a[i * 3 + 2] = col.b;
    }
    g.setAttribute('color', new THREE.BufferAttribute(a, 3));
    g.translate(cx, 0, cz);
    parts.push(g);
    // 1-3 simple palms
    const palms = 1 + ((rnd() * 3) | 0);
    for (let p = 0; p < palms; p++) {
      const px = cx + (rnd() - 0.5) * r, pz = cz + (rnd() - 0.5) * r;
      const ph = 5 + rnd() * 3;
      const trunk = new THREE.CylinderGeometry(0.28, 0.42, ph, 5);
      trunk.translate(px, ph / 2, pz);
      parts.push(paintGeo(trunk, 0xc09a72));
      for (let l = 0; l < 6; l++) {
        const leaf = new THREE.BoxGeometry(4.2, 0.16, 0.9);
        leaf.translate(2.0, 0, 0);
        const mm = new THREE.Matrix4().makeRotationY((l / 6) * Math.PI * 2);
        mm.multiply(new THREE.Matrix4().makeRotationZ(-0.45));
        leaf.applyMatrix4(mm);
        leaf.translate(px, ph, pz);
        parts.push(paintGeo(leaf, 0x6fc98f));
      }
    }
  };

  const count = 5;
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + rnd() * 0.8;
    const dist = half * (1.7 + rnd() * 1.2);
    islet(Math.cos(a) * dist, Math.sin(a) * dist, 14 + rnd() * 16, 2.6 + rnd() * 2);
  }

  const mesh = new THREE.Mesh(
    mergeAll(parts),
    new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.95 })
  );
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  group.add(mesh);
  scene.add(group);
  return { mesh: group, heightAt: () => -6.5, size: terrain.worldSize * 6 };
}

/* ============ vegetation: detailed low-poly trees ============ */
const UPV = new THREE.Vector3(0, 1, 0);
function tube(x0, y0, z0, x1, y1, z1, r0, r1, col, rs = 7) {
  const dir = new THREE.Vector3(x1 - x0, y1 - y0, z1 - z0);
  const len = dir.length();
  const g = new THREE.CylinderGeometry(r1, r0, len, rs);
  g.translate(0, len / 2, 0);
  g.applyMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(new THREE.Quaternion().setFromUnitVectors(UPV, dir.normalize())));
  g.translate(x0, y0, z0);
  return paintGeo(g, col);
}

const C_TREE1 = 0x7fca82, C_TREE2 = 0x66b98a, C_TREE3 = 0x8fd88f, C_TREE4 = 0x9fe49f, C_PALM = 0x6fc98f;

// Pine: curved trunk, branch whorls with needle tufts, 7 fluffy crown layers. ~2m tall at scale 1.
export function buildPineGeo() {
  const parts = [];
  parts.push(tube(0, 0, 0, 0.04, 0.5, 0.02, 0.17, 0.13, 0x9a734f));
  parts.push(tube(0.04, 0.5, 0.02, 0.07, 1.0, -0.02, 0.13, 0.08, 0x8f6a47));
  for (let b = 0; b < 6; b++) {
    const ba = b * 1.05, by = 0.42 + b * 0.1, bl = 0.32 - b * 0.02;
    const ex = Math.cos(ba) * bl, ez = Math.sin(ba) * bl;
    parts.push(tube(0, by, 0, ex, by - 0.06, ez, 0.025, 0.012, 0x8a6748, 4));
    const tuft = new THREE.ConeGeometry(0.09, 0.16, 5);
    tuft.translate(ex, by - 0.02, ez);
    parts.push(paintGeo(tuft, C_TREE2));
  }
  for (let L = 0; L < 7; L++) {
    const t = L / 6, y = 0.55 + t * 1.35, R2 = 0.68 * (1 - t * 0.78);
    for (let k = 0; k < 3; k++) {
      const cn = new THREE.ConeGeometry(R2 * (0.8 + ((L + k) % 3) * 0.12), 0.5 - t * 0.12, 8);
      cn.translate(Math.sin(L * 2.7 + k * 2.1) * R2 * 0.22, y + k * 0.05, Math.cos(L * 1.9 + k * 2.4) * R2 * 0.22);
      parts.push(paintGeo(cn, [C_TREE2, C_TREE1, C_TREE3][(L + k) % 3]));
    }
  }
  const tip = new THREE.ConeGeometry(0.1, 0.3, 6);
  tip.translate(0.02, 2.0, 0);
  parts.push(paintGeo(tip, C_TREE4));
  return mergeAll(parts);
}

// Oak: trunk splitting into 4 real branches, blob crowns in 4 shades, roots + fruit.
export function buildOakGeo() {
  const parts = [];
  parts.push(tube(0, 0, 0, 0.03, 0.55, 0.02, 0.17, 0.12, 0xa07a54));
  for (let w = 0; w < 5; w++) {
    const root = new THREE.ConeGeometry(0.06, 0.28, 4);
    root.rotateZ(1.35); root.rotateY(w * 1.26); root.translate(0, 0.05, 0);
    parts.push(paintGeo(root, 0x93704e));
  }
  const blob = (x, y, z, r, c, d = 1) => {
    const g = new THREE.IcosahedronGeometry(r, d);
    g.scale(1, 0.82, 1); g.translate(x, y, z);
    return paintGeo(g, c);
  };
  const shades = [C_TREE1, C_TREE2, C_TREE3, C_TREE4];
  const tips = [];
  for (let b = 0; b < 4; b++) {
    const ba = b * 1.62 + 0.4, spread = 0.34 + (b % 2) * 0.12;
    const mx = Math.cos(ba) * spread * 0.5, mz = Math.sin(ba) * spread * 0.5;
    const ex = Math.cos(ba) * spread, ez = Math.sin(ba) * spread;
    const ey = 1.15 + (b % 3) * 0.18;
    parts.push(tube(0.03, 0.55, 0.02, mx, 0.9, mz, 0.1, 0.07, 0x9a734f, 5));
    parts.push(tube(mx, 0.9, mz, ex, ey, ez, 0.07, 0.035, 0x8f6a47, 5));
    tips.push([ex, ey, ez]);
  }
  tips.forEach(([x, y, z], i) => {
    parts.push(blob(x, y + 0.14, z, 0.34, shades[i % 4]));
    parts.push(blob(x * 0.75, y + 0.3, z * 0.75, 0.27, shades[(i + 1) % 4]));
  });
  parts.push(blob(0, 1.5, 0, 0.44, C_TREE1, 2));
  parts.push(blob(0.14, 1.72, -0.08, 0.3, C_TREE3));
  parts.push(blob(-0.16, 1.62, 0.12, 0.28, C_TREE2));
  for (let fr = 0; fr < 7; fr++) {
    const fruit = new THREE.IcosahedronGeometry(0.045, 0);
    fruit.translate(Math.sin(fr * 2.4) * 0.45, 1.25 + (fr % 3) * 0.22, Math.cos(fr * 1.9) * 0.4);
    parts.push(paintGeo(fruit, 0xef8f5a));
  }
  return mergeAll(parts);
}

// Palm: curved ringed trunk, coconuts, 11 drooping fronds with leaflets, grass tuft. ~2m at scale 1.
export function buildPalmGeo() {
  const parts = [];
  const segs = 6;
  let topX = 0, topY = 0;
  for (let s = 0; s < segs; s++) {
    const t0 = s / segs, t1 = (s + 1) / segs;
    const x0 = 0.6 * t0 * t0, y0 = 1.9 * t0, x1 = 0.6 * t1 * t1, y1 = 1.9 * t1;
    const len = Math.hypot(x1 - x0, y1 - y0);
    const seg = new THREE.CylinderGeometry(0.05 + 0.045 * (1 - t1), 0.05 + 0.045 * (1 - t0), len, 6);
    seg.translate(0, len / 2, 0);
    seg.applyMatrix4(new THREE.Matrix4().makeRotationZ(-Math.atan2(x1 - x0, y1 - y0)));
    seg.translate(x0, y0, 0);
    parts.push(paintGeo(seg, s % 2 ? 0xc09a72 : 0xb28a62));
    const ring = new THREE.CylinderGeometry(0.062 + 0.045 * (1 - t0), 0.062 + 0.045 * (1 - t0), 0.035, 6);
    ring.translate(x0, y0, 0);
    parts.push(paintGeo(ring, 0xa37c55));
    topX = x1; topY = y1;
  }
  for (let k = 0; k < 3; k++) {
    const nut = new THREE.IcosahedronGeometry(0.085, 0);
    nut.translate(topX + Math.cos(k * 2.1) * 0.13, topY - 0.1, Math.sin(k * 2.1) * 0.13);
    parts.push(paintGeo(nut, 0x8a6748));
  }
  for (let l = 0; l < 11; l++) {
    const yaw = (l / 11) * Math.PI * 2 + (l % 2 ? 0.18 : 0);
    let fx = 0, fy = 0, ang = -0.15 - (l % 3) * 0.1;
    for (let s2 = 0; s2 < 4; s2++) {
      const len = 0.34;
      const rib = new THREE.BoxGeometry(len, 0.028, 0.045);
      rib.translate(len / 2, 0, 0);
      rib.applyMatrix4(new THREE.Matrix4().makeRotationZ(ang));
      rib.translate(fx, fy, 0);
      rib.applyMatrix4(new THREE.Matrix4().makeRotationY(yaw));
      rib.translate(topX, topY + 0.04, 0);
      parts.push(paintGeo(rib, 0x5f9e6b));
      for (const side of [1, -1]) {
        const leaf = new THREE.BoxGeometry(0.30, 0.012, 0.09);
        leaf.translate(0.15, 0, side * 0.07);
        leaf.applyMatrix4(new THREE.Matrix4().makeRotationX(side * 0.85));
        leaf.applyMatrix4(new THREE.Matrix4().makeRotationZ(ang - 0.12));
        leaf.translate(fx + Math.cos(ang) * 0.17, fy + Math.sin(ang) * 0.17, 0);
        leaf.applyMatrix4(new THREE.Matrix4().makeRotationY(yaw));
        leaf.translate(topX, topY + 0.04, 0);
        parts.push(paintGeo(leaf, (l + s2) % 2 ? C_PALM : 0x5db884));
      }
      fx += Math.cos(ang) * 0.34;
      fy += Math.sin(ang) * 0.34;
      ang -= 0.34;
    }
  }
  for (let gv = 0; gv < 5; gv++) {
    const bl2 = new THREE.ConeGeometry(0.035, 0.3, 4);
    bl2.rotateZ((gv - 2) * 0.28); bl2.rotateY(gv * 1.3);
    bl2.translate(Math.sin(gv * 2.2) * 0.12, 0.12, Math.cos(gv * 2.2) * 0.12);
    parts.push(paintGeo(bl2, 0x7cb56a));
  }
  return mergeAll(parts);
}

// Far-LOD billboard for a tree type: a tiny painted silhouette (trunk + foliage) on transparent bg.
function makeBillboardTexture(kind) {
  const c = document.createElement('canvas'); c.width = 96; c.height = 128; const x = c.getContext('2d');
  x.fillStyle = '#b58a5e'; x.fillRect(43, 82, 10, 46);
  const g = x.createLinearGradient(0, 8, 0, 108);
  if (kind === 'pine') { g.addColorStop(0, '#d9f2d0'); g.addColorStop(1, '#93c98a'); }
  else if (kind === 'palm') { g.addColorStop(0, '#d4f0c9'); g.addColorStop(1, '#8fce8a'); }
  else { g.addColorStop(0, '#e2f6dd'); g.addColorStop(1, '#a9dc9f'); }
  x.fillStyle = g;
  const LT = kind === 'pine' ? '#d9f2d0' : kind === 'palm' ? '#d4f0c9' : '#e2f6dd';
  const DK = kind === 'pine' ? '#8fc79a' : kind === 'palm' ? '#84c487' : '#93c98a';
  if (kind === 'pine') {
    const tiers = [[100, 38], [84, 33], [68, 28], [52, 22], [36, 16]];
    tiers.forEach(([by, hw]) => { const ty = by - 30; x.beginPath(); x.moveTo(48, ty); x.lineTo(48 + hw, by); x.lineTo(48 - hw, by); x.closePath(); x.fill(); });
    tiers.forEach(([by, hw]) => { for (let s = -hw; s <= hw + 1; s += hw / 3) { x.beginPath(); x.arc(48 + s, by, 5, 0, 7); x.fill(); } });
    x.fillStyle = LT; x.globalAlpha = 0.5; tiers.forEach(([by, hw]) => { const ty = by - 30; x.beginPath(); x.moveTo(46, ty + 4); x.lineTo(48 - hw * 0.7, by - 2); x.lineTo(46, by - 2); x.closePath(); x.fill(); }); x.globalAlpha = 1;
    x.fillStyle = DK; x.beginPath(); x.moveTo(48, 2); x.lineTo(54, 16); x.lineTo(42, 16); x.closePath(); x.fill();
  } else if (kind === 'palm') {
    x.lineCap = 'round';
    for (let i = 0; i < 8; i++) { const a = Math.PI * (0.1 + 0.8 * (i / 7)); const ex = 48 + Math.cos(a) * 40, ey = 40 - Math.sin(a) * 22 + 16; x.strokeStyle = i % 2 ? LT : '#a6dc9c'; x.lineWidth = 9; x.beginPath(); x.moveTo(48, 40); x.quadraticCurveTo(48 + Math.cos(a) * 22, 20, ex, ey); x.stroke(); x.strokeStyle = DK; x.lineWidth = 3; x.beginPath(); x.moveTo(48, 40); x.quadraticCurveTo(48 + Math.cos(a) * 22, 20, ex, ey); x.stroke(); }
    x.fillStyle = '#9ed69a'; x.beginPath(); x.arc(48, 40, 10, 0, 7); x.fill();
    x.fillStyle = '#8a6a42'; for (const dx of [-7, 6, -1]) { x.beginPath(); x.arc(48 + dx, 50, 4, 0, 7); x.fill(); }
  } else {
    const blobs = [[48, 44, 30], [26, 54, 20], [70, 54, 20], [34, 32, 20], [62, 32, 18], [48, 70, 23], [28, 74, 16], [68, 74, 16]];
    blobs.forEach(([bx, by, r]) => { x.beginPath(); x.arc(bx, by, r, 0, Math.PI * 2); x.fill(); });
    x.fillStyle = LT; x.globalAlpha = 0.55; for (const [bx, by, r] of [[38, 34, 14], [58, 32, 11], [24, 52, 10]]) { x.beginPath(); x.arc(bx, by, r, 0, 7); x.fill(); } x.globalAlpha = 1;
  }
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4; return tex;
}

// Render the ACTUAL 3D tree geo to a transparent, lit + tone-mapped texture (an "impostor") so the far
// billboard matches the real model in colour AND shape. One-off temp renderer per type, then disposed.
let _treeBakeR = null;
function renderTreeImpostor(geo) {
  const W = 136, H = 192, ar = W / H;
  // Reuse ONE shared offscreen renderer across all tree types + new games (avoids extra GL contexts).
  if (!_treeBakeR) {
    _treeBakeR = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    _treeBakeR.setPixelRatio(1); _treeBakeR.outputColorSpace = THREE.SRGBColorSpace;
    _treeBakeR.toneMapping = THREE.ACESFilmicToneMapping; _treeBakeR.toneMappingExposure = 1.5;
  }
  const r = _treeBakeR;
  r.setSize(W, H);
  const scene = new THREE.Scene();
  // Match the in-game lighting so the impostor looks like the tree does in the scene (sun + hemi + fill).
  scene.add(new THREE.HemisphereLight(0xeaf6f0, 0xcfe6c0, 0.7));
  scene.add(new THREE.AmbientLight(0xffffff, 1.35));
  const sun = new THREE.DirectionalLight(0xfff3e0, 1.5); sun.position.set(0.5, 3, 6); scene.add(sun);
  geo.computeBoundingBox(); const bb = geo.boundingBox;
  const cx0 = (bb.min.x + bb.max.x) / 2, cy0 = (bb.min.y + bb.max.y) / 2, cz0 = (bb.min.z + bb.max.z) / 2;
  const gh = bb.max.y - bb.min.y, gw = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z);
  const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.9 }));
  mesh.position.set(-cx0, -cy0, -cz0);
  scene.add(mesh);
  const halfH = Math.max(gh, gw / ar) * 0.55, halfW = halfH * ar;
  const cam = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, 0.1, 100);
  cam.position.set(0, 0, 20); cam.lookAt(0, 0, 0);
  r.render(scene, cam);
  const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
  cv.getContext('2d').drawImage(r.domElement, 0, 0);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4;
  mesh.material.dispose();
  return tex;
}

// Camera-facing billboard material (screen-aligned quads; per-tree aHide to drop cleared trees).
function makeBillboardMaterial(tex) {
  return new THREE.ShaderMaterial({
    uniforms: { map: { value: tex } },
    vertexShader: 'attribute vec2 aCorner; attribute vec2 aSize; attribute float aHide; varying vec2 vUv;' +
      'void main(){ vUv = aCorner + 0.5; vec4 mv = modelViewMatrix * vec4(position,1.0); mv.xy += aCorner*aSize; mv.y -= aHide*100000.0; gl_Position = projectionMatrix*mv; }',
    fragmentShader: 'uniform sampler2D map; varying vec2 vUv; void main(){ vec4 c = texture2D(map, vUv); if (c.a < 0.5) discard; gl_FragColor = vec4(c.rgb, 1.0); }',
    depthWrite: true, side: THREE.DoubleSide,
  });
}

// Shared wind+hide material for a tree type (one GL program; every chunk of that type shares it).
function makeVegMaterial(windShaders) {
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.9 });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = { value: 0 };
    shader.vertexShader = 'uniform float uTime;\nattribute float aSway;\nattribute float aAmp;\nattribute float aHide;\n' + shader.vertexShader
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        transformed.x += sin(uTime*1.6+aSway)*aAmp;
        transformed.z += cos(uTime*1.25+aSway)*aAmp*0.7;
        transformed.y -= aHide * 100000.0;`);
    windShaders.push(shader);
  };
  return mat;
}

// Bake a set of spots into a hi-detail mesh geometry AND a matching billboard geometry (same footprint
// + size), so far chunks swap to cheap camera-facing quads instead of vanishing.
function bakeChunkGeometry(geo, spots, rnd, strength, szA, szB) {
  const parts = [];
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), pv = new THREE.Vector3();
  const bbPos = [], bbCorner = [], bbSize = [], bbHide = [], bbIdx = [], bbRanges = []; let bv = 0;
  const corners = [[-0.5, -0.5], [0.5, -0.5], [0.5, 0.5], [-0.5, 0.5]];
  spots.forEach((sp) => {
    const g = geo.clone();
    const sz = szA + rnd() * (szB - szA);
    const ph = rnd() * Math.PI * 2;
    const nCount = g.attributes.position.count, posA = g.attributes.position;
    const sway = new Float32Array(nCount), amp = new Float32Array(nCount);
    for (let i = 0; i < nCount; i++) { sway[i] = ph; amp[i] = Math.max(posA.getY(i) * sz - 0.7, 0) * 0.05 * strength; }
    g.setAttribute('aSway', new THREE.BufferAttribute(sway, 1));
    g.setAttribute('aAmp', new THREE.BufferAttribute(amp, 1));
    const j = 0.86 + rnd() * 0.28, col = g.attributes.color;
    const jr = j * (0.94 + rnd() * 0.1), jg = j, jb = j * (0.92 + rnd() * 0.1);
    for (let i = 0; i < col.count; i++) col.setXYZ(i, col.getX(i) * jr, col.getY(i) * jg, col.getZ(i) * jb);
    q.setFromAxisAngle(UPV, rnd() * Math.PI * 2);
    m.compose(pv.set(sp[0], sp[1] - 0.08, sp[2]), q, sc.set(sz, sz * (0.9 + rnd() * 0.25), sz));
    g.applyMatrix4(m);
    parts.push(g);
    // Matching billboard quad, centred on the tree's mid-height.
    const H = sz * 2.4, W = sz * 1.7, ccy = (sp[1] - 0.08) + H * 0.46;
    for (const [cxr, cyr] of corners) { bbPos.push(sp[0], ccy, sp[2]); bbCorner.push(cxr, cyr); bbSize.push(W, H); bbHide.push(0); }
    bbIdx.push(bv, bv + 1, bv + 2, bv, bv + 2, bv + 3);
    bbRanges.push({ x: sp[0], z: sp[2], start: bv, count: 4, hidden: false });
    bv += 4;
  });
  if (!parts.length) return null;
  const ranges = []; let off = 0;
  spots.forEach((sp, i) => { const c = parts[i].attributes.position.count; ranges.push({ x: sp[0], z: sp[2], start: off, count: c, hidden: false }); off += c; });
  const merged = mergeAll(parts);
  parts.forEach((p) => p.dispose());
  const hide = new Float32Array(merged.attributes.position.count);
  merged.setAttribute('aHide', new THREE.BufferAttribute(hide, 1));
  merged.computeBoundingSphere();
  const bb = new THREE.BufferGeometry();
  bb.setAttribute('position', new THREE.Float32BufferAttribute(bbPos, 3));
  bb.setAttribute('aCorner', new THREE.Float32BufferAttribute(bbCorner, 2));
  bb.setAttribute('aSize', new THREE.Float32BufferAttribute(bbSize, 2));
  bb.setAttribute('aHide', new THREE.Float32BufferAttribute(bbHide, 1));
  bb.setIndex(bbIdx);
  bb.computeBoundingSphere(); if (bb.boundingSphere) bb.boundingSphere.radius += 12;
  return { geometry: merged, ranges, bbGeometry: bb, bbRanges };
}

// Split spots into a grid; bake each cell as a hi mesh + billboard mesh. three frustum-culls whole
// chunks for free; updateLOD swaps hi↔billboard by distance (billboards never fully removed).
const VEG_CHUNKS = 6;
function bakeChunked(geo, spots, rnd, strength, szA, szB, mat, bbMat, worldSize, group, vegClear, chunkRecords) {
  if (!spots.length) return;
  const halfW = worldSize / 2, cs = worldSize / VEG_CHUNKS;
  const buckets = new Map();
  for (const sp of spots) {
    let ci = Math.floor((sp[0] + halfW) / cs), cj = Math.floor((sp[2] + halfW) / cs);
    ci = Math.max(0, Math.min(VEG_CHUNKS - 1, ci)); cj = Math.max(0, Math.min(VEG_CHUNKS - 1, cj));
    const k = ci * VEG_CHUNKS + cj; if (!buckets.has(k)) buckets.set(k, []); buckets.get(k).push(sp);
  }
  for (const list of buckets.values()) {
    const baked = bakeChunkGeometry(geo, list, rnd, strength, szA, szB);
    if (!baked) continue;
    const mesh = new THREE.Mesh(baked.geometry, mat);
    mesh.castShadow = false; mesh.receiveShadow = false;   // trees no longer cast into the shadow map — biggest safe GPU win (cartoon style forgives it)
    const bb = new THREE.Mesh(baked.bbGeometry, bbMat);
    bb.castShadow = false; bb.receiveShadow = false; bb.visible = false;
    group.add(mesh); group.add(bb);
    vegClear.push({ ranges: baked.ranges, hideAttr: baked.geometry.attributes.aHide });
    vegClear.push({ ranges: baked.bbRanges, hideAttr: baked.bbGeometry.attributes.aHide });
    chunkRecords.push({ hi: mesh, bb, bs: baked.geometry.boundingSphere });
  }
}

export async function addVegetation(scene, terrain, seed = 42) {
  const n = makeNoise(seed);
  const rnd = (() => { let s = seed ^ 0x51ed270b; return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296; })();
  const group = new THREE.Group();
  group.name = 'vegetation';
  const windShaders = [];
  const vegClear = [];

  const half = terrain.worldSize / 2;
  const area = terrain.worldSize * terrain.worldSize;
  const wl = terrain.waterLevel;

  // Spots per type. d = signed distance to the perimeter fence octagon:
  //  d < -3   : zoo grounds — ~95% of the trees live HERE (inside the fence)
  //  -3..+6   : thin wild fringe just outside the fence (sparse)
  //  +6..+13  : beach (palms)
  const pineSpots = [], oakSpots = [], palmSpots = [], rockSpots = [], flowerSpots = [];
  const gateWz = half - terrain.perimeterInset * terrain.cellSize;
  const treeCap = area / 240;
  const tries = Math.floor(area / 22);
  for (let t = 0; t < tries; t++) {
    const wx = (rnd() * 2 - 1) * (half - 2);
    const wz = (rnd() * 2 - 1) * (half - 2);
    const y = terrain.heightAtWorld(wx, wz);
    if (y < wl + 0.35) continue;
    const d = terrain.islandSDF(wx, wz);
    if (Math.abs(wx) < 24 && Math.abs(wz - gateWz) < 20) continue; // keep the entrance clear
    if (d > 6.5) {
      if (y > wl + 0.3 && y < wl + 1.1 && palmSpots.length < area / 2600 && rnd() < 0.5) palmSpots.push([wx, y, wz]);
    } else if (d > -3) {
      // fringe outside the fence: only ~5% of the trees
      const mask = n.fbm((wx + half) / terrain.worldSize * 6, (wz + half) / terrain.worldSize * 6, 3);
      if (mask > 0.1 && rnd() < 0.07 && (pineSpots.length + oakSpots.length) < treeCap) {
        if (rnd() < 0.6) pineSpots.push([wx, y, wz]);
        else oakSpots.push([wx, y, wz]);
      }
    } else {
      // zoo grounds: groves + meadows, dense enough to feel green, open enough to build
      const mask = n.fbm((wx + half) / terrain.worldSize * 5 + 40, (wz + half) / terrain.worldSize * 5 + 40, 3);
      if (mask > 0.12 && (pineSpots.length + oakSpots.length) < treeCap) {
        if (rnd() < 0.45) pineSpots.push([wx, y, wz]);
        else oakSpots.push([wx, y, wz]);
        if (rnd() < 0.45) flowerSpots.push([wx + (rnd() - 0.5) * 6, 0, wz + (rnd() - 0.5) * 6]);
      } else if (mask < -0.55 && rockSpots.length < area / 4200 && rnd() < 0.2) {
        rockSpots.push([wx, y, wz]);
      }
    }
  }
  // A few palms around the lakes
  const lakeList = terrain.lakes ? [...terrain.lakes, terrain.pond] : [terrain.pond];
  for (const lk of lakeList) {
    if (!lk) continue;
    for (let i = 0; i < 4; i++) {
      const a = rnd() * Math.PI * 2;
      const wx = lk.x + Math.cos(a) * (lk.r + 3.5);
      const wz = lk.z + Math.sin(a) * (lk.r + 3.5);
      const y = terrain.heightAtWorld(wx, wz);
      if (y > wl + 0.3 && terrain.islandSDF(wx, wz) < -3) palmSpots.push([wx, y, wz]);
    }
  }
  flowerSpots.forEach((f) => { f[1] = terrain.heightAtWorld(f[0], f[2]); });

  // Trees: unit geos are ~2m tall; island trees are 6.5–11m. Baked into spatial CHUNKS for LOD culling.
  const pineGeo = buildPineGeo(), oakGeo = buildOakGeo(), palmGeo = buildPalmGeo();
  const chunkRecords = [];
  const pineMat = makeVegMaterial(windShaders), oakMat = makeVegMaterial(windShaders), palmMat = makeVegMaterial(windShaders);
  const pineBB = makeBillboardMaterial(renderTreeImpostor(pineGeo)), oakBB = makeBillboardMaterial(renderTreeImpostor(oakGeo)), palmBB = makeBillboardMaterial(renderTreeImpostor(palmGeo));
  bakeChunked(pineGeo, pineSpots, rnd, 0.7, 3.2, 5.4, pineMat, pineBB, terrain.worldSize, group, vegClear, chunkRecords);
  bakeChunked(oakGeo, oakSpots, rnd, 1.0, 3.0, 5.0, oakMat, oakBB, terrain.worldSize, group, vegClear, chunkRecords);
  bakeChunked(palmGeo, palmSpots, rnd, 1.3, 2.6, 4.2, palmMat, palmBB, terrain.worldSize, group, vegClear, chunkRecords);
  pineGeo.dispose(); oakGeo.dispose(); palmGeo.dispose();

  // Rocks: instanced icosahedra with per-instance squash + tint.
  if (rockSpots.length) {
    const rockGeo = paintGeo(new THREE.IcosahedronGeometry(1, 0), 0xb8aa98);
    const rockMat = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.95 });
    const rocks = new THREE.InstancedMesh(rockGeo, rockMat, rockSpots.length);
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), pv = new THREE.Vector3();
    const fc = new THREE.Color();
    rockSpots.forEach((sp, i) => {
      const k = 0.7 + rnd() * 1.6;
      q.setFromAxisAngle(new THREE.Vector3(rnd() - 0.5, 1, rnd() - 0.5).normalize(), rnd() * Math.PI * 2);
      m.compose(pv.set(sp[0], sp[1] - k * 0.25, sp[2]), q, sc.set(k, k * (0.55 + rnd() * 0.4), k));
      rocks.setMatrixAt(i, m);
      const j = 0.88 + rnd() * 0.22;
      rocks.setColorAt(i, fc.setRGB(j, j * (0.97 + rnd() * 0.05), j * 0.95));
    });
    rocks.castShadow = rocks.receiveShadow = true;
    group.add(rocks);
  }

  // Flowers: tiny stem+bud instances in pastel petal colors.
  if (flowerSpots.length) {
    const stem = new THREE.CylinderGeometry(0.03, 0.03, 0.34, 3);
    stem.translate(0, 0.17, 0); paintGeo(stem, 0x7cb56a);
    const bud = new THREE.IcosahedronGeometry(0.13, 0);
    bud.translate(0, 0.42, 0); paintGeo(bud, 0xffffff);
    const flowerGeo = mergeAll([stem, bud]);
    const flowers = new THREE.InstancedMesh(
      flowerGeo,
      new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.9 }),
      flowerSpots.length
    );
    const petals = [0xf2a4b8, 0xf5d98a, 0xb7a8e8, 0xff9d80, 0x8ecbe0];
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), pv = new THREE.Vector3();
    const fc = new THREE.Color();
    flowerSpots.forEach((sp, i) => {
      const k = 0.8 + rnd() * 0.9;
      q.setFromAxisAngle(UPV, rnd() * Math.PI * 2);
      m.compose(pv.set(sp[0], sp[1] - 0.02, sp[2]), q, sc.set(k, k, k));
      flowers.setMatrixAt(i, m);
      flowers.setColorAt(i, fc.setHex(petals[(rnd() * petals.length) | 0]));
    });
    group.add(flowers);
  }

  // Lilypads on the pond + shallow shore water.
  const lilySpots = [];
  for (let t = 0; t < 700 && lilySpots.length < 44; t++) {
    const wx = (rnd() * 2 - 1) * (half - 4);
    const wz = (rnd() * 2 - 1) * (half - 4);
    const y = terrain.heightAtWorld(wx, wz);
    if (y < wl - 0.35 && y > wl - 2 && terrain.islandSDF(wx, wz) < 14) lilySpots.push([wx, wl + 0.03, wz]);
  }
  if (lilySpots.length) {
    const padGeo = new THREE.CircleGeometry(0.55, 9, 0.5, Math.PI * 1.8);
    padGeo.rotateX(-Math.PI / 2);
    paintGeo(padGeo, 0x6fbf6a);
    const pads = new THREE.InstancedMesh(padGeo, new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.8, side: THREE.DoubleSide }), lilySpots.length);
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), pv = new THREE.Vector3();
    lilySpots.forEach((sp, i) => {
      const k = 0.7 + rnd() * 1.1;
      q.setFromAxisAngle(UPV, rnd() * Math.PI * 2);
      m.compose(pv.set(sp[0], sp[1], sp[2]), q, sc.set(k, 1, k));
      pads.setMatrixAt(i, m);
    });
    group.add(pads);
  }

  scene.add(group);
  group.userData.update = (dt) => { windShaders.forEach((s) => { s.uniforms.uTime.value += dt; }); };
  // Tree LOD: full mesh up close, cheap camera-facing BILLBOARD far away (never fully removed).
  // three frustum-culls off-screen chunks for free. Throttled.
  group.userData._lodF = 0;
  group.userData.updateLOD = (cam) => {
    group.userData._lodF++; if (group.userData._lodF % 10 !== 0) return;
    const BB2 = 150 * 150;
    for (const rec of chunkRecords) {
      const bs = rec.bs; if (!bs) continue;
      const far = cam.position.distanceToSquared(bs.center) > BB2 + bs.radius * bs.radius;
      rec.hi.visible = !far;
      rec.bb.visible = far;
    }
  };
  // Actually remove baked trees inside a world rect (buildings) — pushed far underground via aHide.
  const clearRect = (x0, x1, z0, z1) => {
    for (const v of vegClear) {
      let changed = false;
      for (const r of v.ranges) {
        if (r.hidden) continue;
        if (r.x >= x0 && r.x <= x1 && r.z >= z0 && r.z <= z1) {
          for (let k = 0; k < r.count; k++) v.hideAttr.array[r.start + k] = 1;
          r.hidden = true; changed = true;
        }
      }
      if (changed) v.hideAttr.needsUpdate = true;
    }
  };
  window.__clearVegAt = clearRect;
  group.userData.clearRect = clearRect;
  // Remove trees whose cell is in a set of "cx,cz" keys (paths on save load) — one pass over all trees.
  group.userData.clearCells = (cellSet, cs, halfW) => {
    for (const v of vegClear) {
      let changed = false;
      for (const r of v.ranges) {
        if (r.hidden) continue;
        const cx = Math.floor((r.x + halfW) / cs), cz = Math.floor((r.z + halfW) / cs);
        if (cellSet.has(cx + ',' + cz)) {
          for (let k = 0; k < r.count; k++) v.hideAttr.array[r.start + k] = 1;
          r.hidden = true; changed = true;
        }
      }
      if (changed) v.hideAttr.needsUpdate = true;
    }
  };
  // Tree DENSITY (settings): show a deterministic fraction of trees. Position-hashed so the SAME trees
  // hide in both the detail + billboard geometry, and build/path removals (r.hidden) always stay hidden.
  const vegHash = (x, z) => { const s = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453; return s - Math.floor(s); };
  group.userData.setDensity = (frac) => {
    frac = Math.max(0, Math.min(1, frac));
    for (const v of vegClear) {
      for (const r of v.ranges) {
        const val = (r.hidden || vegHash(r.x, r.z) >= frac) ? 1 : 0;
        for (let k = 0; k < r.count; k++) v.hideAttr.array[r.start + k] = val;
      }
      v.hideAttr.needsUpdate = true;
    }
  };
  return group;
}
